/**
 * Pure helper functions for RatesDrawer rate building and form data processing.
 * These are extracted for testability.
 */

import {
  CoverageTierEnum,
  RateDesignEnum,
  RateGender,
  RateUnitEnum,
} from "@platform/openapi-types";

import {
  DEFAULT_EMP_SPOUSE_RATES_DIFFER,
  DEFAULT_TIER_COUNT,
  DEFAULT_TIERS_BY_COUNT,
  RATE_STRUCTURE,
} from "../../constants";
import { getDefaultFormDataForCoverage } from "../../coverage-defaults";
import {
  isBasicLifeCoverageType,
  isLifeCoverageType,
  isStandardPepmCoverageType,
  mapToBackendUnit,
} from "../../utils/rate-helpers";

import type { Rate } from "../../types";
import type { RateScheduleFormData } from "../../types/models";

// UI values that map to the same backend value "employeeDependent"
export const EMPLOYEE_DEPENDENT_VARIANTS = [
  "employeeChild",
  "employeePlusOneDependent",
] as const;

export const getEmployeeDependentVariantForTierCount = (
  tierCount: number,
): string | undefined => {
  if (tierCount === 3) {
    return "employeePlusOneDependent";
  }

  if (tierCount === 5) {
    return "employeeChild";
  }

  return undefined;
};

/**
 * Maps a coverage tier value from the UI representation to the backend representation.
 * Most values are 1:1, except employeeChild and employeePlusOneDependent which both
 * map to "employeeDependent".
 */
export const mapTierToBackendValue = (uiValue: string): string => {
  if (
    EMPLOYEE_DEPENDENT_VARIANTS.includes(
      uiValue as (typeof EMPLOYEE_DEPENDENT_VARIANTS)[number],
    )
  ) {
    return "employeeDependent";
  }
  return uiValue;
};

/**
 * Maps a coverage tier value from the backend representation to the UI representation.
 * For "employeeDependent", returns the preferred variant if specified, otherwise defaults
 * to "employeePlusOneDependent".
 */
export const mapTierFromBackendValue = (
  backendValue: string,
  preferredVariant?: string,
): string => {
  if (backendValue === "employeeDependent") {
    // If a preferred variant is specified and valid, use it
    if (
      preferredVariant &&
      EMPLOYEE_DEPENDENT_VARIANTS.includes(
        preferredVariant as (typeof EMPLOYEE_DEPENDENT_VARIANTS)[number],
      )
    ) {
      return preferredVariant;
    }
    // Default to "employeePlusOneDependent" for new selections
    return "employeePlusOneDependent";
  }
  return backendValue;
};

/** Rate entry type used during rate building */
export type RateEntry = Partial<Rate & { gender: RateGender }>;

/** Context for building rates from form data */
export interface RateBuildContext {
  unit: RateUnitEnum;
  /** Unit for dependent-tier rows; "usd" when dependents are unit-based, else mirrors `unit`. */
  dependentUnit: RateUnitEnum;
  ageBands: { lower: number; upper: number }[];
  tiers: CoverageTierEnum[];
  structureDetail?: string;
  empSpouseRatesDiffer: boolean;
  /** True when "Differ? = No" — primary schedule gets separate EE/SP records for volume tracking */
  empSpouseNoCase?: boolean;
  isFmlaPepm: boolean;
  isStandardPepm: boolean;
  /** Whether dependent rates are included (flat rate only, defaults to false) */
  includeDependentRates?: boolean;
  /** Whether dependent rates are combined or separate (flat rate only) */
  dependentRateType?: "combined" | "separate";
}

/**
 * Normalizes age bands before saving:
 * - Ensures the last age band has upper=150 (implicit max age)
 */
export const normalizeAgeBandsForSave = (
  formData: RateScheduleFormData,
): RateScheduleFormData => {
  const ageBands = formData.ageBandRanges;
  if (!ageBands || ageBands.length === 0) {
    return formData;
  }

  // Clone the age bands and set the last one's upper to 150
  const normalizedBands = ageBands.map((band, index) => {
    if (index === ageBands.length - 1) {
      return { ...band, upper: 150 };
    }
    return band;
  });

  return { ...formData, ageBandRanges: normalizedBands };
};

/** PEPM unit types that should not be used for flat rates */
const PEPM_UNITS = [
  RateUnitEnum.totalPepm,
  RateUnitEnum.fmlaPepm,
  RateUnitEnum.adaPepm,
];

/**
 * Normalizes the rate unit when switching between rate structures.
 * - If switching to flat but unit is a PEPM unit, reset to "usd"
 * - If switching to PEPM but unit is not a PEPM unit, clear it (will be set by buildPepmRates)
 */
export const normalizeUnitForSave = (
  formData: RateScheduleFormData,
): RateScheduleFormData => {
  const { rateStructure, unit } = formData;

  // If flat rate structure but unit is a PEPM unit, reset to "usd"
  if (
    rateStructure === "flat" &&
    unit &&
    PEPM_UNITS.includes(unit as RateUnitEnum)
  ) {
    return { ...formData, unit: RateUnitEnum.usd };
  }

  // If PEPM structure, the unit will be set by buildPepmRates, so we can leave it
  // (the context builder defaults to "usd" for non-flat anyway)

  return formData;
};

/**
 * Fills an unset "EMP & Spouse Rates Differ" with the default the radio displays,
 * so the saved columns match what the analyst sees selected.
 */
export const normalizeEmpSpouseRatesDifferForSave = (
  formData: RateScheduleFormData,
): RateScheduleFormData => {
  const isQuestionShown =
    formData.rateStructure === RATE_STRUCTURE.AGE_BANDED &&
    formData.includeDependentRates === true;
  if (!isQuestionShown || formData.empSpouseRatesDiffer) {
    return formData;
  }
  return { ...formData, empSpouseRatesDiffer: DEFAULT_EMP_SPOUSE_RATES_DIFFER };
};

/**
 * Computes backend rate design from UI form fields.
 */
export const computeRateDesign = (
  formData: RateScheduleFormData,
): RateDesignEnum | undefined => {
  switch (formData.rateStructure) {
    case "tiered":
      return RateDesignEnum.composite;
    case "flat":
    case "pepm":
      return RateDesignEnum.flatRate;
    case "ageBanded":
      return computeAgeBandedDesign(formData.structureDetail);
    default:
      return undefined;
  }
};

/**
 * Computes the specific age-banded design based on structure detail.
 */
export const computeAgeBandedDesign = (
  structureDetail?: string,
): RateDesignEnum => {
  switch (structureDetail) {
    case "tiered":
      return RateDesignEnum.ageBandedTiered;
    case "tobacco":
      return RateDesignEnum.ageBandedTobacco;
    case "gender":
      return RateDesignEnum.ageBandedGender;
    default:
      return RateDesignEnum.ageBanded;
  }
};

const isPlainAgeBanded = (formData: RateScheduleFormData): boolean =>
  formData.rateStructure === RATE_STRUCTURE.AGE_BANDED &&
  (formData.structureDetail ?? "none") === "none";

/**
 * Creates the context object for rate building from form data.
 */
export const createRateBuildContext = (
  formData: RateScheduleFormData,
  coverageType?: string,
  isDependentRateBasisEnabled = false,
): RateBuildContext => {
  const includeDependentRates = formData.includeDependentRates === true;

  // Use formData.unit for flat and age-banded; tiered and other structures always use USD
  const unit =
    formData.rateStructure === "flat" || formData.rateStructure === "ageBanded"
      ? ((formData.unit || "usd") as unknown as RateUnitEnum)
      : ("usd" as unknown as RateUnitEnum);
  const ageBands = formData.ageBandRanges || [];
  // Map UI tier values to backend values (e.g., employeeChild/employeePlusOneDependent -> employeeDependent)
  const tiers = (formData.selectedTiers || [])
    .filter((tier) => tier && tier.trim() !== "")
    .map((tier) =>
      mapTierToBackendValue(tier),
    ) as unknown as CoverageTierEnum[];

  const lowerCaseCoverageType = coverageType?.toLowerCase() ?? "";
  const isStandardPepm =
    formData.rateStructure === "pepm" &&
    lowerCaseCoverageType !== "" &&
    isStandardPepmCoverageType(lowerCaseCoverageType);

  // Independent dependent rate basis is Basic Life only, and gated behind
  // BTX_DEPENDENT_RATE_BASIS; every other case keeps the legacy behavior of
  // dependents always mirroring EE's unit.
  const dependentUnit =
    isDependentRateBasisEnabled &&
    includeDependentRates &&
    isBasicLifeCoverageType(coverageType) &&
    formData.dependentRatesBasedOn === "unit"
      ? (RateUnitEnum.usd as RateUnitEnum)
      : unit;

  return {
    unit,
    dependentUnit,
    ageBands,
    tiers,
    structureDetail: formData.structureDetail,
    empSpouseRatesDiffer:
      includeDependentRates && formData.empSpouseRatesDiffer === "yes",
    // Only plain age-banded moves the spouse onto the primary schedule on No; other designs keep it on the companion.
    empSpouseNoCase:
      includeDependentRates &&
      formData.empSpouseRatesDiffer === "no" &&
      isPlainAgeBanded(formData),
    isFmlaPepm:
      lowerCaseCoverageType === "fmla" && formData.rateStructure === "pepm",
    isStandardPepm,
    includeDependentRates,
    dependentRateType: formData.dependentRateType,
  };
};

/**
 * Builds flat rate entries (including PEPM variants).
 * When includeDependentRates is true, appends dependent rate entries
 * after the employee rate(s) using coverageTierCode.
 */
export const buildFlatRates = (ctx: RateBuildContext): RateEntry[] => {
  if (ctx.isStandardPepm || ctx.isFmlaPepm) {
    return buildPepmRates(ctx);
  }

  let employeeRates: RateEntry[];

  if (ctx.structureDetail === "gender") {
    employeeRates = [
      { unit: ctx.unit, rate: 0, gender: RateGender.male },
      { unit: ctx.unit, rate: 0, gender: RateGender.female },
    ];
  } else if (ctx.structureDetail === "tobacco") {
    employeeRates = [
      { unit: ctx.unit, rate: 0, isTobaccoRated: false },
      { unit: ctx.unit, rate: 0, isTobaccoRated: true },
    ];
  } else {
    employeeRates = [{ unit: ctx.unit, rate: 0 }];
  }

  if (!ctx.includeDependentRates) {
    return employeeRates;
  }

  const dependentRates = buildDependentRateEntries(
    ctx.dependentUnit,
    ctx.dependentRateType,
  );
  return [...employeeRates, ...dependentRates];
};

/**
 * Builds dependent rate entries for flat rate designs.
 * Combined: single "employeeDependent" tier.
 * Separate: "spouseOnly" + "childOnly" tiers.
 */
export const buildDependentRateEntries = (
  unit: RateUnitEnum,
  dependentRateType?: "combined" | "separate",
): RateEntry[] => {
  if (dependentRateType === "separate") {
    return [
      { unit, rate: 0, coverageTierCode: CoverageTierEnum.spouseOnly },
      { unit, rate: 0, coverageTierCode: CoverageTierEnum.childOnly },
    ];
  }
  // Default to combined
  return [
    { unit, rate: 0, coverageTierCode: CoverageTierEnum.employeeDependent },
  ];
};

/**
 * Builds PEPM rates (FMLA and standard coverage types).
 */
export const buildPepmRates = (ctx: RateBuildContext): RateEntry[] => {
  const pepmUnits = [
    RateUnitEnum.fmlaPepm,
    RateUnitEnum.adaPepm,
    RateUnitEnum.totalPepm,
  ];

  if (ctx.structureDetail === "gender") {
    return pepmUnits.flatMap((unit) => [
      { unit, rate: 0, numberOfLives: 0, gender: RateGender.male },
      { unit, rate: 0, numberOfLives: 0, gender: RateGender.female },
    ]);
  }

  return pepmUnits.map((unit) => ({ unit, rate: 0, numberOfLives: 0 }));
};

/**
 * Builds age-banded rates, optionally split by employee/spouse.
 * Note: child rates live in a separate companion schedule built by
 * buildCompanionRateScheduleRates(), not in this function's output.
 */
export const buildAgeBandedRates = (ctx: RateBuildContext): RateEntry[] => {
  const bands = ctx.ageBands.length > 0 ? ctx.ageBands : [null];
  const lastIdx = bands.length - 1;

  return bands.flatMap((band, i) => {
    const baseRate: RateEntry = { unit: ctx.unit, rate: 0 };
    if (band) {
      const omitLower = i === 0 && band.lower === 0;
      const omitUpper = i === lastIdx && band.upper === 150;
      if (!omitLower) baseRate.ageBandLower = band.lower;
      if (!omitUpper) baseRate.ageBandUpper = band.upper;
    }

    if (ctx.empSpouseRatesDiffer || ctx.empSpouseNoCase) {
      return [
        { ...baseRate, coverageTierCode: CoverageTierEnum.employee },
        {
          ...baseRate,
          unit: ctx.dependentUnit,
          coverageTierCode: CoverageTierEnum.spouseOnly,
        },
      ];
    }
    return [baseRate];
  });
};

/**
 * Builds a companion rate schedule rates.
 */
export const buildCompanionRateScheduleRates = (
  ctx: RateBuildContext,
): RateEntry[] => {
  const rates: RateEntry[] = [];
  if (!ctx.includeDependentRates) {
    return rates;
  }

  if (ctx.dependentRateType === "combined") {
    // When empSpouseNoCase, spouseOnly is already in the primary schedule, so companion only covers children
    rates.push({
      unit: ctx.dependentUnit,
      rate: 0,
      coverageTierCode: ctx.empSpouseNoCase
        ? CoverageTierEnum.childOnly
        : CoverageTierEnum.spouseChildren,
    });
  } else if (ctx.dependentRateType === "separate") {
    rates.push({
      unit: ctx.dependentUnit,
      rate: 0,
      coverageTierCode: CoverageTierEnum.childOnly,
    });
  }

  return rates;
};

/**
 * Builds age-banded tobacco rates.
 */
export const buildAgeBandedTobaccoRates = (
  ctx: RateBuildContext,
): RateEntry[] => {
  const bands = ctx.ageBands.length > 0 ? ctx.ageBands : [null];
  const tobaccoOptions = [false, true];

  return bands.flatMap((band) => {
    const baseRate: RateEntry = { unit: ctx.unit, rate: 0 };
    if (band) {
      baseRate.ageBandLower = band.lower;
      baseRate.ageBandUpper = band.upper;
    }

    return tobaccoOptions.flatMap((isTobaccoRated) => {
      if (ctx.empSpouseRatesDiffer) {
        return [
          {
            ...baseRate,
            isTobaccoRated,
            coverageTierCode: CoverageTierEnum.employee,
          },
          {
            ...baseRate,
            unit: ctx.dependentUnit,
            isTobaccoRated,
            coverageTierCode: CoverageTierEnum.spouseOnly,
          },
        ];
      }
      return [{ ...baseRate, isTobaccoRated }];
    });
  });
};

/**
 * Builds age-banded gender rates.
 */
export const buildAgeBandedGenderRates = (
  ctx: RateBuildContext,
): RateEntry[] => {
  const bands = ctx.ageBands.length > 0 ? ctx.ageBands : [null];
  const genders = [RateGender.male, RateGender.female];

  return bands.flatMap((band) => {
    const baseRate: RateEntry = { unit: ctx.unit, rate: 0 };
    if (band) {
      baseRate.ageBandLower = band.lower;
      baseRate.ageBandUpper = band.upper;
    }

    return genders.flatMap((gender) => {
      if (ctx.empSpouseRatesDiffer) {
        return [
          { ...baseRate, gender, coverageTierCode: CoverageTierEnum.employee },
          {
            ...baseRate,
            unit: ctx.dependentUnit,
            gender,
            coverageTierCode: CoverageTierEnum.spouseOnly,
          },
        ];
      }
      return [{ ...baseRate, gender }];
    });
  });
};

/**
 * Builds age-banded tiered rates.
 */
export const buildAgeBandedTieredRates = (ctx: RateBuildContext): RateEntry[] =>
  ctx.ageBands.flatMap((band) =>
    ctx.tiers.map((tier) => ({
      unit: ctx.unit,
      rate: 0,
      ageBandLower: band.lower,
      ageBandUpper: band.upper,
      coverageTierCode: tier,
    })),
  );

/**
 * Builds composite (tiered) rates.
 */
export const buildCompositeRates = (ctx: RateBuildContext): RateEntry[] =>
  ctx.tiers.map((tier) => ({
    unit: ctx.unit,
    rate: 0,
    coverageTierCode: tier,
  }));

/** Band-only key (no tier code) for migration fallback in mergeExistingRateValues */
const bandKey = (r: Partial<Rate>): string => {
  const lower = r.ageBandLower === 0 ? undefined : r.ageBandLower;
  const upper = r.ageBandUpper === 150 ? undefined : r.ageBandUpper;
  return `${lower}|${upper}|${r.unit}|${r.isTobaccoRated}|${r.gender}`;
};

/** Pairs an EE row with its SP row; unit is left out because SP can use the dependent unit. */
const empSpousePairKey = (r: Partial<Rate>): string => {
  const lower = r.ageBandLower === 0 ? undefined : r.ageBandLower;
  const upper = r.ageBandUpper === 150 ? undefined : r.ageBandUpper;
  return `${lower}|${upper}|${r.isTobaccoRated}|${r.gender}`;
};

/**
 * Produces a lookup key from the structural dimensions of a rate entry.
 * Rate values (rate, volume) are intentionally excluded — this key identifies
 * the slot, not the value.
 *
 * Sentinel boundary values are normalized so that stored rates (which may carry
 * explicit 0 / 150 from normalizeAgeBandsForSave) match freshly-built desired
 * rates (which omit those boundary values in buildAgeBandedRates):
 *   ageBandLower === 0   → treated as absent (first band)
 *   ageBandUpper === 150 → treated as absent (last band)
 */
export const rateKey = (r: Partial<Rate>): string => {
  const lower = r.ageBandLower === 0 ? undefined : r.ageBandLower;
  const upper = r.ageBandUpper === 150 ? undefined : r.ageBandUpper;
  return `${lower}|${upper}|${r.unit}|${r.coverageTierCode}|${r.isTobaccoRated}|${r.gender}`;
};

/**
 * Lookup key using all rate dimensions except ageBandUpper. Used as a fallback
 * index for legacy rates where the last band was stored with an explicit upper
 * (e.g. 99) rather than null/150.
 */
const partialRateKey = (r: Partial<Rate>): string => {
  const lower = r.ageBandLower === 0 ? undefined : r.ageBandLower;
  return `${lower}|${r.unit}|${r.coverageTierCode}|${r.isTobaccoRated}|${r.gender}`;
};

/** Distinct coverage tiers in the order their rates appear, as the drawer lists them. */
const tiersInOrder = (rates: readonly Partial<Rate>[]): CoverageTierEnum[] => [
  ...new Set(
    rates.flatMap((r) => (r.coverageTierCode ? [r.coverageTierCode] : [])),
  ),
];

/**
 * Maps each relabeled tier slot to the tier it replaced. With the tier count
 * unchanged, a slot whose tier is new to the schedule, and whose old tier is
 * no longer selected anywhere, is the same slot under a corrected label (e.g.
 * "Employee & 2 Children" changed to "Full Family"), so it keeps its values.
 */
export const relabeledTiers = (
  desiredRates: readonly RateEntry[],
  existingRates: readonly Rate[],
): Map<CoverageTierEnum, CoverageTierEnum> => {
  const desiredTiers = tiersInOrder(desiredRates);
  const existingTiers = tiersInOrder(existingRates);
  if (desiredTiers.length !== existingTiers.length) return new Map();

  return new Map(
    desiredTiers.flatMap(
      (tier, idx): [CoverageTierEnum, CoverageTierEnum][] => {
        const replaced = existingTiers[idx];
        const isRelabeled =
          !existingTiers.includes(tier) && !desiredTiers.includes(replaced);
        return isRelabeled ? [[tier, replaced]] : [];
      },
    ),
  );
};

/**
 * Merges user-entered rate, lives, and volume values from existing rates into
 * the desired (zero-initialised) rate structure. Matching is done by structural
 * key so that only slots with the same age bands, unit, coverage tier, tobacco
 * flag, and gender are carried over, plus the fallbacks below for slots whose
 * key shifted without the slot changing meaning.
 *
 * Used whenever the rate design is unchanged, so a config edit (tiers, age
 * bands, dependent or AD&D rates) keeps the values of every slot it leaves in
 * place and only new slots start at zero. `replacedTierBySlotTier` comes from
 * relabeledTiers, for designs where the analyst selects the coverage tiers.
 */
export const mergeExistingRateValues = (
  desiredRates: RateEntry[],
  existingRates: Rate[],
  replacedTierBySlotTier: ReadonlyMap<
    CoverageTierEnum,
    CoverageTierEnum
  > = new Map(),
): RateEntry[] => {
  const exactByKey = new Map(existingRates.map((r) => [rateKey(r), r]));

  // Appending a band closes the old open-ended last band (65+ becomes 65–69);
  // it is still the band starting at that age, so it keeps its values.
  const openEndedByPartialKey = new Map(
    existingRates
      .filter((r) => r.ageBandUpper == null || r.ageBandUpper === 150)
      .map((r) => [partialRateKey(r), r]),
  );

  // Legacy last-band fallback index: maps partial key (all dimensions except upper)
  // to the existing rate with the highest explicit upper, for rates that were stored
  // with a literal upper (e.g. 99) instead of null/150. Only used when a desired
  // last-band slot (upper omitted or 150) has no exact match.
  const legacyLastBandByPartialKey = new Map<string, Rate>();
  for (const r of existingRates) {
    if (r.ageBandUpper == null || r.ageBandUpper === 150) continue;
    const partial = partialRateKey(r);
    const current = legacyLastBandByPartialKey.get(partial);
    if (!current || r.ageBandUpper > current.ageBandUpper!) {
      legacyLastBandByPartialKey.set(partial, r);
    }
  }

  // Tier migration fallback: existing records from old "No" plans had no
  // coverageTierCode; when the plan is migrated to EE/SP structure the desired
  // rates carry a tier code but the stored rates don't — match by band key only.
  // A desired untiered row means the untiered slot still exists (a flat
  // employee rate beside new dependent rows), so nothing was migrated.
  const isMigratingToTiers = desiredRates.every((r) => r.coverageTierCode);
  const existingByBandKey = new Map(
    isMigratingToTiers
      ? existingRates
          .filter((r) => !r.coverageTierCode)
          .map((r) => [bandKey(r), r])
      : [],
  );

  // Unit fallback: an SP row moved onto the dependent unit keeps its rate, but not its volume, since the quantity basis changed.
  const existingSpouseByPairKey = new Map(
    existingRates
      .filter((r) => r.coverageTierCode === CoverageTierEnum.spouseOnly)
      .map((r) => [empSpousePairKey(r), r]),
  );

  /** The saved rate for a slot, by exact key or by one of the age-band fallbacks. */
  const findSavedSlot = (slot: RateEntry): Rate | undefined => {
    const exact = exactByKey.get(rateKey(slot));
    if (exact) return exact;

    // Open-ended last-band fallback: the desired band is the last/unbounded one
    // (upper absent or 150), so try the legacy candidate.
    const isOpenEnded = slot.ageBandUpper == null || slot.ageBandUpper === 150;
    return isOpenEnded
      ? legacyLastBandByPartialKey.get(partialRateKey(slot))
      : openEndedByPartialKey.get(partialRateKey(slot));
  };

  return desiredRates.map((desired) => {
    const replacedTier =
      desired.coverageTierCode &&
      replacedTierBySlotTier.get(desired.coverageTierCode);
    let existing =
      findSavedSlot(desired) ??
      (replacedTier
        ? findSavedSlot({ ...desired, coverageTierCode: replacedTier })
        : undefined);

    // Tier migration fallback: desired has a tier code but existing doesn't.
    if (!existing && desired.coverageTierCode) {
      existing = existingByBandKey.get(bandKey(desired));
    }

    const spouseWithOtherUnit =
      !existing && desired.coverageTierCode === CoverageTierEnum.spouseOnly
        ? existingSpouseByPairKey.get(empSpousePairKey(desired))
        : undefined;
    if (spouseWithOtherUnit?.rate !== undefined) {
      return { ...desired, rate: spouseWithOtherUnit.rate };
    }

    if (!existing) return desired;

    const merged: RateEntry = { ...desired };
    if (existing.rate !== undefined) merged.rate = existing.rate;
    if (existing.numberOfLives !== undefined)
      merged.numberOfLives = existing.numberOfLives;
    if (existing.volume !== undefined) merged.volume = existing.volume;
    return merged;
  });
};

/**
 * Copies the EE rate to the matching SP record for each age band.
 * Called after mergeExistingRateValues when empSpouseNoCase is true so that
 * previously-different EE/SP rates are unified before saving.
 * Only rate is equalized — volume tracks enrolled lives separately per tier.
 */
export const equalizeEmpSpouseRates = (rates: RateEntry[]): RateEntry[] => {
  const eeByBand = new Map(
    rates
      .filter((r) => r.coverageTierCode === CoverageTierEnum.employee)
      .map((r) => [empSpousePairKey(r), r]),
  );
  return rates.map((r) => {
    if (r.coverageTierCode !== CoverageTierEnum.spouseOnly) return r;
    const ee = eeByBand.get(empSpousePairKey(r));
    return ee != null ? { ...r, rate: ee.rate } : r;
  });
};

/**
 * Infers whether saved age-banded rates encode differing employee/spouse
 * values, mirroring the "EMP & Spouse Rates Differ" form field. Employee and
 * spouse-only rows always exist as separate records once dependent rates are
 * included, regardless of that field's value, so the only way to recover it
 * from saved data is by comparing the entered rate values per band.
 */
export const inferEmpSpouseRatesDiffer = (
  rates: Pick<
    Rate,
    | "rate"
    | "coverageTierCode"
    | "ageBandLower"
    | "ageBandUpper"
    | "gender"
    | "isTobaccoRated"
  >[],
): boolean => {
  const someRatesEntered = rates.some((r) => r.rate != null && r.rate !== 0);
  return (
    someRatesEntered &&
    rates.some((r) => {
      if (r.coverageTierCode !== CoverageTierEnum.employee) return false;
      const spRate = rates.find(
        (s) =>
          s.coverageTierCode === CoverageTierEnum.spouseOnly &&
          s.ageBandLower === r.ageBandLower &&
          s.ageBandUpper === r.ageBandUpper &&
          s.gender === r.gender &&
          s.isTobaccoRated === r.isTobaccoRated,
      );
      return spRate !== undefined && r.rate !== spRate.rate;
    })
  );
};

type RateUnitStructure = "flat" | "ageBanded";

const RATE_UNIT_STRUCTURES: RateUnitStructure[] = [
  RATE_STRUCTURE.FLAT,
  RATE_STRUCTURE.AGE_BANDED,
] as RateUnitStructure[];

const holdsRateUnitFields = (
  structure: RateScheduleFormData["rateStructure"],
): structure is RateUnitStructure =>
  RATE_UNIT_STRUCTURES.includes(structure as RateUnitStructure);

const pickRateUnitFields = (
  formData: RateScheduleFormData,
): Pick<
  RateScheduleFormData,
  "coveredBenefitType" | "ratePer" | "ratesBasedOn" | "unit"
> => ({
  coveredBenefitType: formData.coveredBenefitType,
  ratePer: formData.ratePer,
  ratesBasedOn: formData.ratesBasedOn,
  unit: formData.unit,
});

/**
 * Rate unit fields for a flat or age-banded structure: what that structure was
 * last left holding, or the coverage's own defaults the first time it is
 * selected. Flat and age-banded share the shape of these fields but not their
 * values, so STD can sit on Per $100 as flat and Per $10 as age banded.
 */
const resolveRateUnitFields = (
  held:
    | Pick<
        RateScheduleFormData,
        "coveredBenefitType" | "ratePer" | "ratesBasedOn" | "unit"
      >
    | undefined,
  defaults: RateScheduleFormData | undefined,
): Partial<RateScheduleFormData> => {
  if (held?.ratesBasedOn === "unit") {
    return { ...held, unit: held.unit ?? "usd" };
  }
  if (held?.ratePer && held.coveredBenefitType) {
    return held;
  }

  const coveredBenefitType = defaults?.coveredBenefitType ?? "coveredBenefit";
  const ratePer = defaults?.ratePer ?? "per1000";

  return {
    coveredBenefitType,
    ratePer,
    ratesBasedOn: defaults?.ratesBasedOn ?? "volume",
    unit: mapToBackendUnit(coveredBenefitType, ratePer),
  };
};

/** Rate unit and dependent fields, cleared for tiered and PEPM structures. */
const CLEARED_RATE_UNIT_FIELDS: Partial<RateScheduleFormData> = {
  ratesBasedOn: undefined,
  coveredBenefitType: undefined,
  ratePer: undefined,
  unit: undefined,
  includeDependentRates: undefined,
  dependentRateType: undefined,
};

/**
 * Structure detail and tier fields for the structure being selected. Age
 * banded carries the tier fields too when the coverage's detail is tiered,
 * as Critical Illness's is.
 */
const resolveStructureFields = (
  value: (typeof RATE_STRUCTURE)[keyof typeof RATE_STRUCTURE],
  defaults: RateScheduleFormData | undefined,
): Partial<RateScheduleFormData> => {
  const isTiered =
    value === RATE_STRUCTURE.TIERED ||
    (value === RATE_STRUCTURE.AGE_BANDED &&
      defaults?.structureDetail === "tiered");
  const tierCount = defaults?.numberOfTiers ?? DEFAULT_TIER_COUNT;

  return {
    structureDetail:
      value === RATE_STRUCTURE.AGE_BANDED
        ? (defaults?.structureDetail ?? "none")
        : undefined,
    numberOfTiers: isTiered ? tierCount : undefined,
    selectedTiers: isTiered
      ? (defaults?.selectedTiers ?? DEFAULT_TIERS_BY_COUNT[tierCount])
      : undefined,
  };
};

/**
 * Computes the next form data when the user picks a different rate structure.
 *
 * Fields that only apply to the previous structure are cleared. The coverage's
 * own defaults are applied when they describe the structure being selected, so
 * returning to Age Banded restores STD's Per $10 rather than the generic
 * Per $1000.
 */
export const applyRateStructureChange = (
  prev: RateScheduleFormData,
  value: (typeof RATE_STRUCTURE)[keyof typeof RATE_STRUCTURE],
  coverageType?: string,
): RateScheduleFormData => {
  const coverageDefaults = getDefaultFormDataForCoverage(coverageType);
  // Structure-specific fields only carry over when the coverage's own default
  // is the structure being selected.
  const structureDefaults =
    coverageDefaults.rateStructure === value ? coverageDefaults : undefined;
  const rateUnitDefaults = coverageDefaults.coveredBenefitType
    ? coverageDefaults
    : undefined;

  // Remember what the outgoing structure held before moving off it.
  const rateUnitByStructure = holdsRateUnitFields(prev.rateStructure)
    ? {
        ...prev.rateUnitByStructure,
        [prev.rateStructure]: pickRateUnitFields(prev),
      }
    : prev.rateUnitByStructure;

  const isFlat = value === RATE_STRUCTURE.FLAT;
  const isAgeBanded = value === RATE_STRUCTURE.AGE_BANDED;

  const updates: Partial<RateScheduleFormData> = {
    rateStructure: value,
    ...resolveStructureFields(value, structureDefaults),
    ...(isFlat || isAgeBanded
      ? resolveRateUnitFields(
          holdsRateUnitFields(value) ? rateUnitByStructure?.[value] : undefined,
          rateUnitDefaults,
        )
      : CLEARED_RATE_UNIT_FIELDS),
    rateUnitByStructure,
  };

  if (
    isAgeBanded &&
    !prev.ageBandRanges?.length &&
    structureDefaults?.ageBandRanges
  ) {
    updates.ageBandRanges = structureDefaults.ageBandRanges;
  }

  // AD&D support follows the coverage type, not the selected rate structure.
  if (!isLifeCoverageType(coverageType)) {
    updates.adndRatesApply = false;
    updates.dependentAdndRatesApply = false;
    updates.dependentAdndType = undefined;
  }

  return { ...prev, ...updates };
};
